import { useState, useRef, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Building2, ChevronLeft, ChevronRight, Search, Star, SlidersHorizontal, X } from 'lucide-react';
import { cn } from '@/lib/utils';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import NavigationGroup from './NavigationGroup';
import { avisoSePinta, contenidoDelBadge, textoAccesibleDelAviso } from './AvisoPendientes';
import { useMediaQuery } from '@/hooks/use-media-query';
import { usePinnedModules } from '@/lib/usePinnedModules';
import { activeNavigationItem, navigationHref, navigationId, visibleNavigation } from '@/lib/navigation';
import { useNavigationContext } from './useNavigationContext';
import SidebarFavorites from './SidebarFavorites';
import CommitDeLaBuild from './CommitDeLaBuild';

function leerBadge(valor) {
  if (valor && typeof valor === 'object') {
    return {
      // Las dos condiciones las resuelve `avisoSePinta`, que es la misma que usa el aviso:
      // un badge visible con el texto accesible vacío sería un punto rojo que no dice nada.
      visible: avisoSePinta(valor),
      contenido: contenidoDelBadge(valor),
      texto: textoAccesibleDelAviso(valor.textos),
    };
  }
  const n = valor || 0;
  return { visible: n > 0, contenido: n > 99 ? '99+' : String(n), texto: `${n} pendientes` };
}


export default function Sidebar({ navigation, badges = {}, currentPageName, collapsed, setCollapsed,
  mobileMenuOpen, setMobileMenuOpen, expandedMenus, setExpandedMenus, currentCompany,
  setShowCompanySwitcher, onCmdK }) {
  const { t } = useTranslation();
  const location = useLocation();
  const navCtx = useNavigationContext();
  const favorites = usePinnedModules();
  const { pinned, isPinned, toggle: togglePin, isBusy } = favorites;
  const isMobile = useMediaQuery('(max-width: 1023.98px)');
  const isDrawer = isMobile;
  const isEffectivelyCollapsed = !isMobile && collapsed;
  const [desktopGroup, setDesktopGroup] = useState(null);
  const [locked, setLocked] = useState(false);
  const [favoritesOpen, setFavoritesOpen] = useState(false);
  const favoritesButtonRef = useRef(null);
  const enterTimer = useRef(null);
  const leaveTimer = useRef(null);
  const closeBtnRef = useRef(null);
  const sidebarRef = useRef(null);
  const visibleNav = visibleNavigation(navigation, navCtx);
  const leaves = visibleNav.flatMap(item => item.subItems || [item]);
  const favoriteOptions = visibleNav.flatMap(item => item.subItems
    ? item.subItems.map(sub => ({ ...sub, favoriteGroup: item.nameKey }))
    : ['context', 'setup'].includes(item.zone) ? [] : [item])
    .map(item => ({ ...item, nameKey: item.nameKeyFor ? item.nameKeyFor(navCtx) : item.nameKey, favoriteHref: navigationHref(item, navCtx) }));
  const activeItem = activeNavigationItem(visibleNav, location, navCtx, currentPageName);
  const activeId = activeItem && navigationId(activeItem);
  const activeGroupKey = visibleNav.find(item => item.subItems?.some(sub => navigationId(sub) === activeId))?.nameKey;
  const pinnedItems = pinned.map(id => leaves.find(item => navigationId(item) === id)).filter(Boolean);
  const clearTimers = () => { clearTimeout(enterTimer.current); clearTimeout(leaveTimer.current); };
  const closePanel = () => { clearTimers(); setDesktopGroup(null); setLocked(false); };
  const navigate = () => { closePanel(); setFavoritesOpen(false); setMobileMenuOpen(false); };
  const enter = key => {
    clearTimers();
    if (key && !locked) enterTimer.current = setTimeout(() => { setDesktopGroup(key); setLocked(false); }, 180);
  };
  const leave = () => {
    clearTimers();
    if (!locked) leaveTimer.current = setTimeout(() => { setDesktopGroup(null); }, 300);
  };
  useEffect(() => () => { clearTimeout(enterTimer.current); clearTimeout(leaveTimer.current); }, []);
  useEffect(() => {
    clearTimeout(enterTimer.current); clearTimeout(leaveTimer.current);
    setDesktopGroup(null); setLocked(false); setFavoritesOpen(false);
  }, [location.pathname, location.search, currentCompany?.id, isMobile, collapsed]);
  useEffect(() => {
    if (isMobile) setExpandedMenus(activeGroupKey ? { [activeGroupKey]: true } : {});
  }, [activeGroupKey, isMobile, setExpandedMenus]);
  useEffect(() => {
    if (!isDrawer || !mobileMenuOpen) return undefined;
    const onKey = event => {
      if (event.target instanceof Element && event.target.closest('[data-favorites-manager]')) return;
      if (event.key === 'Escape') setMobileMenuOpen(false);
      if (event.key === 'Tab') {
        const nodes = sidebarRef.current?.querySelectorAll('a[href],button:not([disabled])');
        const visible = [...(nodes || [])].filter(node => node.getClientRects().length > 0);
        const first = visible[0], last = visible.at(-1);
        if (event.shiftKey && document.activeElement === first) { event.preventDefault(); last?.focus(); }
        else if (!event.shiftKey && document.activeElement === last) { event.preventDefault(); first?.focus(); }
      }
    };
    document.addEventListener('keydown', onKey);
    const previousFocus = document.activeElement;
    const previous = document.body.style.overflow;
    document.body.style.overflow = 'hidden'; closeBtnRef.current?.focus();
    return () => { document.removeEventListener('keydown', onKey); document.body.style.overflow = previous; if (previousFocus instanceof HTMLElement && previousFocus.isConnected) previousFocus.focus(); };
  }, [isDrawer, mobileMenuOpen, setMobileMenuOpen]);
  useEffect(() => { if (!isMobile && mobileMenuOpen) setMobileMenuOpen(false); }, [isMobile, mobileMenuOpen, setMobileMenuOpen]);

  const itemBadge = item => leerBadge(item.badgeKey === false ? null : badges[item.badgeKey || item.pageName]);
  const renderLeaf = (item, detail = false, shortcut = false) => {
    const id = navigationId(item);
    const isActive = activeId === id;
    const pinnedHere = isPinned(id);
    const label = t(item.nameKeyFor ? item.nameKeyFor(navCtx) : item.nameKey);
    const favoriteLabel = t(pinnedHere ? 'navigation.favorites.remove' : 'navigation.favorites.add', { name: label });
    const { visible: badgeVisible, contenido: badgeContenido, texto: badgeTexto } = itemBadge(item);
    // Popovers always show labels, including when the global rail is collapsed.
    const showLabel = detail || !isEffectivelyCollapsed;
    return <div key={id} className="group flex items-center relative rounded-xl hover:bg-sidebar-accent/70">
      <Link to={navigationHref(item, navCtx)} onClick={navigate}
        aria-current={isActive ? 'page' : undefined}
        aria-label={!showLabel ? t(item.nameKeyFor ? item.nameKeyFor(navCtx) : item.nameKey) : undefined}
        title={!showLabel ? t(item.nameKey) : undefined}
        className={cn('flex-1 min-w-0 flex items-center gap-3 px-3 rounded-xl transition-colors text-sm focus-visible:ring-2 focus-visible:ring-ring',
          isDrawer || detail ? 'py-2.5' : 'py-2', isActive && 'bg-sidebar-accent text-foreground font-medium', !showLabel && 'justify-center')}
      >
        <span className="relative shrink-0"><item.icon className={cn('w-5 h-5', isActive ? 'text-primary' : 'opacity-75')} />
          {isEffectivelyCollapsed && badgeVisible && !detail && <span
            className="absolute -top-1.5 -right-1.5 min-w-4 h-4 px-1 inline-flex items-center justify-center rounded-full bg-primary text-primary-foreground text-[9px] font-semibold tabular-nums"
            role="img" aria-label={badgeTexto} data-testid={`badge-plegado-${item.pageName}`}>{badgeContenido}</span>}
        </span>
        {showLabel && <span className="flex-1 min-w-0"><span className="flex items-center gap-1.5">{t(item.nameKeyFor ? item.nameKeyFor(navCtx) : item.nameKey)}
          {item.beta && <Badge variant="secondary" className="h-4 px-1.5 text-[10px]">{t('common.beta')}</Badge>}</span>
          {detail && item.descriptionKey && <span className="block mt-0.5 text-xs font-normal text-muted-foreground">{t(item.descriptionKey)}</span>}
        </span>}
        {!isEffectivelyCollapsed && badgeVisible && <span className="min-w-[18px] h-[18px] px-1 inline-flex items-center justify-center rounded-full bg-primary/15 text-primary text-nano font-semibold tabular-nums"
          role="img" aria-label={badgeTexto} data-testid={`badge-${item.pageName}`}>{badgeContenido}</span>}
        {isEffectivelyCollapsed && detail && badgeVisible && <span className="text-xs text-primary" role="img" aria-label={badgeTexto}>{badgeContenido}</span>}
      </Link>
      {showLabel && item.zone !== 'setup' && (!shortcut || pinnedHere) && <button type="button"
        onClick={() => togglePin(id)} disabled={isBusy}
        aria-label={favoriteLabel} aria-pressed={pinnedHere}
        title={favoriteLabel}
        className={cn('mr-1 p-2 rounded-lg hover:bg-sidebar-accent focus-visible:opacity-100 focus-visible:ring-2 focus-visible:ring-ring disabled:opacity-30',
          isDrawer && 'min-h-11 min-w-11 grid place-items-center',
          pinnedHere || isDrawer ? 'opacity-100' : 'opacity-0 group-hover:opacity-100 group-focus-within:opacity-100')}
        data-testid={`pin-${id}`}>
        {pinnedHere ? <Star className="h-4 w-4 fill-primary text-primary" /> : <Star className="h-4 w-4 text-muted-foreground" />}
      </button>}
    </div>;
  };
  const renderItem = item => item.subItems ? <NavigationGroup key={item.nameKey} item={item}
    mobile={isMobile} collapsed={isEffectivelyCollapsed}
    groupBadge={item.subItems.map(itemBadge).find(badge => badge.visible)}
    active={item.subItems.some(sub => navigationId(sub) === activeId)}
    open={isMobile ? !!expandedMenus[item.nameKey] : desktopGroup === item.nameKey} locked={locked}
    onEnter={enter} onLeave={leave} renderLeaf={renderLeaf}
    onOpen={(key, pin) => {
      clearTimers();
      if (isMobile) setExpandedMenus({ [key]: true });
      else { setDesktopGroup(key); setLocked(pin); }
    }}
    onClose={() => { if (isMobile) setExpandedMenus({}); else closePanel(); }}
    onLock={() => setLocked(value => !value)} /> : renderLeaf(item);

  return <><aside ref={sidebarRef} data-testid="layout-sidebar" aria-label={t('common.sidebar', 'Barra lateral')}
    role={isDrawer ? 'dialog' : undefined} aria-modal={isDrawer && mobileMenuOpen && !favoritesOpen ? 'true' : undefined}
    className={cn('fixed text-sidebar-foreground border border-sidebar-border shadow-elev-2 flex flex-col overflow-hidden transition-[width,transform] duration-200',
      isDrawer ? 'bg-sidebar z-modal inset-y-0 left-0 rounded-r-3xl w-[19rem] max-w-[90vw]' : 'bg-sidebar/95 backdrop-blur-2xl z-50 top-3 left-3 bottom-3 rounded-3xl',
      favoritesOpen && 'z-40',
      !isDrawer && (isEffectivelyCollapsed ? 'w-16' : 'w-64'),
      mobileMenuOpen ? 'translate-x-0' : '-translate-x-full invisible lg:translate-x-0 lg:visible')}
  >
    <div className="border-b border-sidebar-border">
      <div className="flex items-center pr-2"><button type="button" data-testid="company-switcher"
        onClick={() => { navigate(); setShowCompanySwitcher(true); }} aria-label={t('common.switch_company', 'Cambiar empresa')}
        className={cn('flex-1 min-w-0 p-4 flex items-center gap-3 min-h-16 hover:bg-sidebar-accent', isEffectivelyCollapsed && 'justify-center')}>
        {currentCompany?.logo_url ? <img src={currentCompany.logo_url} alt={currentCompany.name} className="w-9 h-9 rounded-lg object-cover shrink-0" />
          : <span className="w-9 h-9 rounded-xl bg-primary/15 text-primary grid place-items-center shrink-0">{currentCompany?.theme?.initial?.toUpperCase() || currentCompany?.name?.[0]?.toUpperCase() || <Building2 className="h-5 w-5" />}</span>}
        {!isEffectivelyCollapsed && <span className="min-w-0 text-left"><span className="block text-sm font-semibold truncate">{currentCompany?.name}</span><span className="block text-xs text-muted-foreground">{t('common.switch_company')}</span></span>}
      </button>
      {isDrawer && <button ref={closeBtnRef} type="button" onClick={() => setMobileMenuOpen(false)} aria-label={t('common.close_menu', 'Cerrar menú')} data-testid="sidebar-close" className="p-3 rounded-full hover:bg-sidebar-accent"><X className="h-5 w-5" /></button>}</div>
      {/* Reserve the declared context row before its authorized link resolves. */}
      <div className="px-2 pb-2"><div className={navigation.some(item => item.zone === 'context') ? (isDrawer ? 'min-h-10' : 'min-h-9') : undefined}>{visibleNav.filter(item => item.zone === 'context').map(item => renderLeaf(item, false, true))}</div></div>
    </div>
    {onCmdK && <div className="px-2 pt-3 pb-2"><button type="button" onClick={() => { closePanel(); setMobileMenuOpen(false); onCmdK(); }}
      aria-label={t('common.search_placeholder')} data-testid="sidebar-cmdk"
      className={cn('h-9 w-full flex items-center gap-2 px-3 rounded-xl border border-sidebar-border bg-sidebar-accent/50 text-sm text-muted-foreground hover:bg-sidebar-accent', isEffectivelyCollapsed && 'justify-center px-0')}>
      <Search className="h-4 w-4 shrink-0" />{!isEffectivelyCollapsed && <><span className="flex-1 text-left">{t('common.search_placeholder')}</span><kbd className="text-nano">{typeof navigator !== 'undefined' && /Mac|iPhone|iPad/.test(navigator.platform) ? '⌘K' : 'Ctrl K'}</kbd></>}
    </button></div>}
    <nav data-testid="navigation-menu" aria-label={t('common.navigation', 'Navegación principal')} className="flex-1 min-h-0 overflow-y-auto p-2 space-y-1">
      {visibleNav.filter(item => ['start', 'setup', 'daily'].includes(item.zone)).map(renderItem)}
      <section aria-label={t('navigation.favorites.title')} className="pt-3 pb-2">
        <div className={cn('flex items-center gap-2 px-3 pb-1', isEffectivelyCollapsed && 'px-0 justify-center')}>
          {!isEffectivelyCollapsed && <span className="flex-1 text-nano font-semibold uppercase tracking-wider text-muted-foreground">{t('navigation.favorites.title')}</span>}
          <button ref={favoritesButtonRef} type="button" onClick={() => { closePanel(); setFavoritesOpen(true); }}
            aria-label={t('navigation.favorites.manage')} title={t('navigation.favorites.manage')}
            className={cn('shrink-0 grid place-items-center rounded-lg hover:bg-sidebar-accent focus-visible:ring-2 focus-visible:ring-ring', isDrawer ? 'h-11 w-11' : 'h-9 w-9')}>
            {isEffectivelyCollapsed ? <Star className="h-4 w-4" /> : <SlidersHorizontal className="h-4 w-4" />}
          </button>
        </div>
        {pinnedItems.slice(0, 3).map(item => renderLeaf(item, false, true))}
        {!isEffectivelyCollapsed && pinnedItems.length === 0 && <button type="button" onClick={() => { closePanel(); setFavoritesOpen(true); }}
          className="w-full text-left px-3 py-2 text-xs text-muted-foreground hover:text-foreground focus-visible:ring-2 focus-visible:ring-ring">{t('navigation.favorites.empty')}</button>}
        {!isEffectivelyCollapsed && pinnedItems.length > 3 && <button type="button" onClick={() => { closePanel(); setFavoritesOpen(true); }}
          className="w-full text-left rounded-lg px-3 py-2 text-xs text-muted-foreground hover:bg-sidebar-accent focus-visible:ring-2 focus-visible:ring-ring">{t('navigation.favorites.view_all', { count: pinnedItems.length })}</button>}
      </section>
      {!isEffectivelyCollapsed && <div className="px-3 pt-3 pb-1 text-nano font-semibold uppercase tracking-wider text-muted-foreground">{t('navigation.work')}</div>}
      {visibleNav.filter(item => item.zone === 'work').map(renderItem)}
    </nav>
    <div className="shrink-0 p-2 space-y-1 border-t border-sidebar-border">
      {visibleNav.filter(item => item.zone === 'system').map(renderItem)}
      {visibleNav.filter(item => item.zone === 'personal').map(item => renderLeaf(item, false, true))}
      {!isDrawer && <><Button variant="ghost" size="sm" onClick={() => setCollapsed(!collapsed)} className="w-full" aria-label={collapsed ? 'Expandir barra lateral' : 'Colapsar barra lateral'}>
        {isEffectivelyCollapsed ? <ChevronRight className="h-5 w-5" /> : <><ChevronLeft className="h-5 w-5 mr-2" />{t('common.collapse')}</>}
      </Button><CommitDeLaBuild oculto={isEffectivelyCollapsed} /></>}
    </div>
  </aside>
    <SidebarFavorites key={currentCompany?.id} open={favoritesOpen} onOpenChange={setFavoritesOpen}
      items={favoriteOptions} favorites={favorites} onNavigate={navigate} returnFocusRef={favoritesButtonRef} />
  </>;
}
